import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import { formatPhoneNumber, isValidateEmail, isValidatePassword, isValidPhoneNumber } from 'constants/regularExpression';
import { postForgetPasswordAPI, postForgetPasswordConfirmAPI, putForgetPasswordChangeAPI } from 'apis/verify/verifyAPI';
import { useTranslation } from 'react-i18next';

interface FindIdPopupProps {
  isOpen: boolean;
  handleClose: () => void;
}

export default function FindPwPopup({ isOpen, handleClose }: FindIdPopupProps) {
  const [step, setStep] = useState<'default' | 'verification' | 'reset' | 'completed'>('default');

  const [type, setType] = useState('NATIVE'); // 사용자의 내/외국인 타입 상태 추가 (이메일, 전화번호 인증버튼에 필요)
  const [name, setName] = useState(''); // 이름 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberAlert, setPhoneNumberAlert] = useState(true); // 휴대폰번호 유효성 상태 (기본값 true)
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false); // 사용자가 휴대폰번호 필드를 벗어났는지 여부
  const [email, setEmail] = useState(''); // 이메일 상태
  const [emailAlert, setEmailAlert] = useState(true); // 이메일 유효성 상태 (기본값 true)
  const [emailTouched, setEmailTouched] = useState(false); // 사용자가 이메일 필드를 벗어났는지 여부
  const [username, setUsername] = useState(''); // 아이디 상태

  const [verificationCode, setVerificationCode] = useState(''); // 인증코드 값

  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false); // 비밀번호 확인 필드가 포커스를 벗어났는지 여부

  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordAlert, setPasswordAlert] = useState(true); // 비밀번호 유효성 상태
  const [errormsg, setErrormsg] = useState(''); // 에러메세지 상태
  const [confirmPasswordAlert, setConfirmPasswordAlert] = useState(true); // 비밀번호 일치 여부
  const [verificationToken, setVerificationToken] = useState(''); // 비밀번호 토큰 상태

  // 다국어
  const { t } = useTranslation();

  // 핸드폰 번호
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = event.target.value?.replace(/[^0-9]/g, '');
    setPhoneNumber(formatPhoneNumber(phoneNumber));
  };

  const handlePhoneNumberBlur = () => {
    setPhoneNumberTouched(true); // 사용자가 필드를 떠났다는 표시
    setPhoneNumberAlert(isValidPhoneNumber(phoneNumber)); // 유효성 검사를 실행
  };

  // 이메일 필드에서 포커스를 벗어날 때 호출되는 함수
  const handleEmailBlur = () => {
    setEmailTouched(true); // 사용자가 필드를 떠났다는 표시
    setEmailAlert(isValidateEmail(email)); // 유효성 검사를 실행
  };

  // 인증 버튼 클릭 핸들러
  const handleCertify = async () => {
    setErrormsg('');

    const data = {
      type,
      // name,
      username,
      ...(type === 'NATIVE'
        ? { phoneNumber } // 내국인일 경우
        : { email }) // 외국인일 경우
    };

    try {
      // API
      const result = await postForgetPasswordAPI(data);
      // console.log('인증버튼 클릭 후 받은 결과(인증코드) : ', result.data.data, result);
    } catch (error: any) {
      // console.error('Error:', error);
      const errorMessage = error?.data.message || '알 수 없는 오류가 발생했습니다.';

      setErrormsg(errorMessage);
      return;
    } // 인증코드를 입력하는 UI를 보여주도록 상태를 변경
    setStep('verification'); // 인증 클릭 시 비밀번호 재설정 화면으로 전환
  };

  // 인증코드 확인 버튼 클릭 핸들러
  const handleVerification = async () => {
    setErrormsg('');

    // 입력한 인증 코드를 처리하는 로직
    // console.log('입력된 인증코드:', verificationCode);
    const data = {
      username,
      verificationCode: verificationCode
    };
    try {
      // API
      const result = await postForgetPasswordConfirmAPI(data);
      if (result.data.code == 200) {
        const resetPasswordResult = await putForgetPasswordChangeAPI({
          username: username,
          verificationToken: result.data.data.verificationToken
        });
        // console.log('resetPasswordResult:', resetPasswordResult);

        // setStep('reset'); // 인증 클릭 시 비밀번호 재설정 화면으로 전환
        setStep('completed'); // 바로 비밀번호 초기화하는걸로 로직 바뀜
      }
    } catch (error: any) {
      const errorMessage = error?.data.message || t('find_pw_page.message.error');
      setErrormsg(errorMessage);
      return;
    }
  };

  // 재설정 버튼 클릭 시 완료 화면으로 전환
  const handleReset = async () => {
    const data = {
      username,
      verificationToken,
      newPassword: password
    };
    try {
      // API
      await putForgetPasswordChangeAPI(data);
    } catch (error: any) {
      const errorMessage = error?.data.message || t('find_pw_page.message.error');
      setErrormsg(errorMessage);
      return;
    }

    setStep('completed');
    // if (password === confirmPassword && password.length > 0) {
    //   setStep('completed');
    // } else {
    //   alert('비밀번호가 일치하지 않거나 입력이 잘못되었습니다.');
    // }
  };
  // 닫기 버튼과 함께 모든 상태를 리셋하는 함수
  const handleCloseAndReset = () => {
    setStep('default');
    setType('NATIVE');
    setName('');
    setPhoneNumber('');
    setPhoneNumberAlert(true);
    setPhoneNumberTouched(false);
    setEmail('');
    setEmailAlert(true);
    setEmailTouched(false);
    setUsername('');
    setVerificationCode('');
    setPassword('');
    setConfirmPassword('');
    setErrormsg('');
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };
  // 비밀번호 입력 시 상태 업데이트 및 유효성 검사
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputPassword = event.target.value;
    setPassword(inputPassword);
    setPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };
  // 비밀번호 확인 필드에서 포커스를 벗어날 때 호출
  const handleConfirmPasswordBlur = () => {
    setPasswordTouched(true); // 사용자가 필드를 벗어났음을 표시
    setConfirmPasswordAlert(password === confirmPassword); // 비밀번호가 일치하는지 확인
  };
  return (
    <Dialog open={isOpen} onClose={handleCloseAndReset} transitionDuration={0}>
      <DialogTitle className="headerTitle">
        {/* 비밀번호 재설정 타이틀 */}
        <span>{t('find_pw_page.title')}</span>
        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ width: '400px', maxWidth: '400px', paddingY: '20px !important' }}>
        {step !== 'reset' && step !== 'completed' && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              {/* 내/외국인 */}
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_pw_page.type')}
              </Typography>
              <RadioGroup
                row
                value={type} // userType 상태 사용
                onChange={(e) => setType(e.target.value)} // 상태 업데이트
                name="type"
                sx={{ height: 32 }}
              >
                {/* 내국인 */}
                <FormControlLabel
                  value="NATIVE"
                  control={<Radio size="small" sx={{ height: 32 }} />}
                  label={t('find_pw_page.phone')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
                {/* 외국인 */}
                <FormControlLabel
                  value="FOREIGNER"
                  control={<Radio size="small" sx={{ height: 32 }} />}
                  label={t('find_pw_page.email')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </RadioGroup>
            </Box>
            {/* 이름 */}
            <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_pw_page.name')}
              </Typography>
              <TextField
                required
                name="name"
                size="small"
                placeholder={t('find_pw_page.message.name')}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  flexGrow: 1,
                  width: '100px',
                  '& .MuiInputBase-root': {
                    // 내부의 input 요소에 스타일 적용
                    height: '32px',
                    borderRadius: '4px',
                    '& input': {
                      height: '32px',
                      fontSize: '0.75rem',
                      padding: '0 8px'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginX: '8px'
                  }
                }}
              />
            </Box>
            {/* 아이디 */}
            <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_pw_page.id')}
              </Typography>
              <TextField
                required
                name="username"
                size="small"
                placeholder={t('find_pw_page.message.id')}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  flexGrow: 1,
                  width: '100px',
                  '& .MuiInputBase-root': {
                    // 내부의 input 요소에 스타일 적용
                    height: '32px',
                    borderRadius: '4px',
                    '& input': {
                      height: '32px',
                      fontSize: '0.75rem',
                      padding: '0 8px'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginX: '8px'
                  }
                }}
              />
            </Box>

            {type === 'NATIVE' ? (
              // 내국인일 경우 휴대전화 입력 폼
              <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
                <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                  {t('find_pw_page.phone')}
                </Typography>
                <TextField
                  required
                  name="phoneNumber"
                  id="phoneNumber"
                  type="tel"
                  size="small"
                  placeholder={t('find_pw_page.message.phone')}
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  onBlur={handlePhoneNumberBlur} // 포커스를 벗어날 때 유효성 검사
                  error={phoneNumberTouched && !phoneNumberAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={phoneNumberTouched && !phoneNumberAlert ? t('find_pw_page.message.phone_wrong') : null} // 오류 메시지
                  inputProps={{
                    inputMode: 'numeric', // 모바일에서 숫자키패드
                    pattern: '[0-9]*' // 숫자만 입력
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.75rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
            ) : (
              // 외국인일 경우 이메일 입력 폼
              <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
                <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                  {t('find_pw_page.email')}
                </Typography>
                <TextField
                  required
                  name="email"
                  id="email"
                  type="email"
                  size="small"
                  placeholder={t('find_pw_page.message.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)} // 이메일 상태 업데이트
                  onBlur={handleEmailBlur} // 포커스를 벗어날 때 유효성 검사
                  error={emailTouched && !emailAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={emailTouched && !emailAlert ? t('find_pw_page.message.email_wrong') : null} // 오류 메시지
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.75rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
            )}
            {/* 인증코드 */}
            {step === 'verification' && (
              <Box flexGrow={1} display="flex" flexDirection="row" alignItems="start" gap={1}>
                <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                  {t('find_pw_page.certify_code')}
                </Typography>
                <TextField
                  required
                  size="small"
                  placeholder={t('find_pw_page.message.certify_code')}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // 기본 엔터 동작 막기
                      handleVerification();
                    }
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.75rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
            )}

            {errormsg && (
              <Alert severity="warning" sx={{ paddingY: '2px' }}>
                {errormsg}
              </Alert>
            )}
          </Box>
        )}
        {/* 비밀번호 */}
        {step === 'reset' && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_pw_page.pw')}
              </Typography>
              <TextField
                aria-labelledby="password-label"
                fullWidth
                // label="비밀번호"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // margin="normal"
                size="small"
                variant="outlined"
                placeholder={t('find_pw_page.message.pw')}
                onBlur={() => setPasswordTouched(true)}
                error={passwordTouched && !passwordAlert} // 필드가 떠난 후에만 에러 표시
                helperText={passwordTouched && !passwordAlert ? t('find_pw_page.message.pw_validity') : null}
                sx={{
                  flexGrow: 1,
                  width: '100px',
                  '& .MuiInputBase-root': {
                    // 내부의 input 요소에 스타일 적용
                    height: '32px',
                    borderRadius: '4px',
                    '& input': {
                      height: '32px',
                      fontSize: '0.75rem',
                      padding: '0 8px'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginX: '8px'
                  }
                }}
              />
            </Box>
            {/* 비밀번호 확인  */}
            <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_pw_page.pw_check')}
              </Typography>
              <TextField
                aria-labelledby="confirm-password-label"
                fullWidth
                // label="비밀번호 확인"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                // margin="normal"
                size="small"
                variant="outlined"
                placeholder={t('find_pw_page.message.pw_check')}
                onBlur={handleConfirmPasswordBlur} // 포커스를 벗어날 때 유효성 검사 실행
                error={passwordTouched && !confirmPasswordAlert} // 필드가 떠난 후에만 에러 표시
                helperText={passwordTouched && !confirmPasswordAlert ? t('find_pw_page.message.pw_wrong') : null}
                sx={{
                  flexGrow: 1,
                  width: '100px',
                  '& .MuiInputBase-root': {
                    // 내부의 input 요소에 스타일 적용
                    height: '32px',
                    borderRadius: '4px',
                    '& input': {
                      height: '32px',
                      fontSize: '0.75rem',
                      padding: '0 8px'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginX: '8px'
                  }
                }}
              />
            </Box>
          </Box>
        )}
        {step === 'completed' && (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              {t('find_pw_page.message.completed1')}
            </Typography>
            <Typography variant="body1" gutterBottom mt={2}>
              {t('find_pw_page.message.completed2')}
              <br />
              {t('find_pw_page.message.completed3')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      {/* 확인 버튼 */}
      <DialogActions sx={{ px: '24px', pb: '20px', pt: 0 }}>
        {step === 'default' && (
          // 인증 버튼
          <Button variant="contained" size="medium" color="primary" fullWidth onClick={handleCertify} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
            {t('find_pw_page.btn.certify')}
          </Button>
        )}
        {step === 'verification' && (
          // 다음
          <Button fullWidth variant="contained" size="medium" color="primary" onClick={handleVerification} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
            {t('find_pw_page.btn.next')}
          </Button>
        )}
        {step === 'reset' && (
          // 재설정
          <Button variant="contained" size="medium" color="primary" fullWidth onClick={handleReset} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
            {t('find_pw_page.btn.reset')}
          </Button>
        )}
        {step === 'completed' && (
          // 로그인 하기
          <Button variant="contained" size="medium" color="primary" fullWidth onClick={handleCloseAndReset} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
            {t('find_pw_page.btn.log_in')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
